import { useRovingFocus } from "./useRovingFocus";

interface SortPillOption {
  id: string;
  label: string;
}

interface SortPillsProps {
  /** Criterios de orden ("Monto", "Sobregiro", "Desviación"). */
  options: SortPillOption[];
  /** `id` del criterio activo. */
  value: string;
  onChange(id: string): void;
  /** Rótulo visible antes de las pills ("Ordenar por"). Si no se pasa, no se muestra. */
  label?: string;
  /** Nombre accesible del grupo. */
  ariaLabel: string;
}

/** Selector de criterio: radio group de pills; el activo va en amarillo con `aria-checked`. Las flechas (←→ y ↑↓) mueven la selección. */
export function SortPills({ options, value, onChange, label, ariaLabel }: SortPillsProps) {
  const ids = options.map((o) => o.id);
  const { onKeyDown, setElement } = useRovingFocus(ids, onChange, { vertical: true });

  return (
    <div role="radiogroup" aria-label={ariaLabel} className="flex flex-wrap items-center gap-2">
      {label && (
        <span aria-hidden="true" className="mr-1 text-[11px] font-medium uppercase leading-[14px] tracking-[0.14em] text-[var(--ds-ink-muted)]">
          {label}
        </span>
      )}
      {options.map((o, index) => {
        const checked = o.id === value;
        return (
          <button
            key={o.id}
            ref={setElement(index)}
            type="button"
            role="radio"
            aria-checked={checked}
            tabIndex={checked ? 0 : -1}
            onClick={() => onChange(o.id)}
            onKeyDown={(event) => onKeyDown(event, index)}
            className={`app-focus-ring inline-flex h-9 cursor-pointer items-center rounded-full border-[1.5px] px-4 text-[15px] leading-none whitespace-nowrap ${
              checked
                ? "border-[var(--ds-card-sun)] bg-[var(--ds-card-sun)] font-medium text-[var(--ds-on-card)]"
                : "border-[var(--ds-ink)] bg-transparent text-[var(--ds-ink)]"
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
